import { useMemo } from 'react';

import { useUserData } from '@givebacks/api/core';
import {
  useCreateConsumerContactCampaignOrganizer,
  useCreateConsumerContactUs,
} from '@givebacks/api/notification';
import { useShowConsumerCampaignById } from '@givebacks/api/store';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Alert,
  ButtonMantine,
  Center,
  FullWidthButtonGroup,
  Modal,
  ModalProps,
  Stack,
  TextInput,
  Textarea,
  notifications,
} from 'ui';
import { UseFormReturnType, isEmail, isNotEmpty, useForm } from 'ui/Form';

import {
  useFormListValidation,
  useFormListValidator,
} from '../../../../hooks/validators/useListValidator';
import { getErrorMessage_v2 } from '../../../../utils';

interface ContactOrganizerModalProps extends ModalProps {
  causeId?: string;
  campaignId?: string;
}

type ContactOrganizerForm = {
  first_name: string;
  last_name: string;
  email: string;
  note: string;
  recaptcha?: string | null;
};

const ContactOrganizerForm = () => {
  const { data: user } = useUserData();

  const form = useForm<ContactOrganizerForm>({
    initialValues: {
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      email: user?.email ?? '',
      note: '',
    },
    validate: {
      first_name: isNotEmpty('First Name is required'),
      last_name: isNotEmpty('Last Name is required'),
      email: isEmail('Valid email is required'),
      note: isNotEmpty('Note to organizer is required'),
      recaptcha: (value) => (!value ? true : null),
    },
    validateInputOnBlur: true,
  });

  useFormListValidator({ form, id: 'contactOrganizerForm' });

  return (
    <Stack>
      <TextInput label="First Name" required {...form.getInputProps('first_name')} />
      <TextInput label="Last Name" required {...form.getInputProps('last_name')} />
      <TextInput label="Email" required {...form.getInputProps('email')} />
      <Textarea
        label="Note"
        required
        autosize
        minRows={4}
        maxRows={4}
        {...form.getInputProps('note')}
      />
      <Stack gap="xs">
        <Center>
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_KEY}
            onChange={(key) => form.setFieldValue('recaptcha', key)}
          />
        </Center>

        <Alert.Collapse in={!!form.errors.recaptcha} type="error" title=" reCaptcha must pass!" />
      </Stack>
    </Stack>
  );
};

const ContactOrganizerFormFooter = ({
  causeId,
  campaignId,
  onClose,
}: {
  causeId?: string;
  campaignId?: string;
  onClose: () => void;
}) => {
  const { validateForms, useGetForm } = useFormListValidation<{
    contactOrganizerForm: UseFormReturnType<ContactOrganizerForm>;
  }>();

  const form = useGetForm('contactOrganizerForm');

  const { data: campaign } = useShowConsumerCampaignById({ campaignId, causeId });

  const { mutate: contactCampaignOrganizer, status: contactCampaignOrganizerStatus } =
    useCreateConsumerContactCampaignOrganizer();
  const { mutate: contactSeller, status: contactSellerStatus } = useCreateConsumerContactUs();

  const handleOnSave = () => {
    if (causeId && validateForms().isValid) {
      if (campaign) {
        contactCampaignOrganizer(
          { causeId, campaignName: campaign?.name, user: { ...form.values } },
          {
            onSuccess() {
              notifications.show({
                variant: 'success',
                message: 'Your form has successfully been submitted.',
              });
              onClose();
            },
            onError(e) {
              notifications.show({ variant: 'error', message: getErrorMessage_v2(e) });
            },
          }
        );
      } else {
        contactSeller(
          { causeId, user: { ...form.values } },
          {
            onSuccess() {
              notifications.show({
                variant: 'success',
                message: 'Your form has successfully been submitted.',
              });
              onClose();
            },
            onError(e) {
              notifications.show({ variant: 'error', message: getErrorMessage_v2(e) });
            },
          }
        );
      }
    }
  };

  const isRequestPending = useMemo(() => {
    return contactCampaignOrganizerStatus === 'pending' || contactSellerStatus === 'pending';
  }, [contactCampaignOrganizerStatus, contactSellerStatus]);

  return (
    <Modal.AffixedFooter>
      <FullWidthButtonGroup>
        <ButtonMantine variant="subtle" onClick={onClose} disabled={isRequestPending}>
          Cancel
        </ButtonMantine>
        <ButtonMantine onClick={handleOnSave} loading={isRequestPending}>
          Send
        </ButtonMantine>
      </FullWidthButtonGroup>
    </Modal.AffixedFooter>
  );
};

const ContactOrganizerModal = ({
  title,
  opened,
  onClose,
  causeId,
  campaignId,
}: ContactOrganizerModalProps) => {
  return (
    <Modal opened={opened} onClose={onClose} title={title ?? 'Contact Organizer'}>
      <ContactOrganizerForm />
      <ContactOrganizerFormFooter onClose={onClose} causeId={causeId} campaignId={campaignId} />
    </Modal>
  );
};

export { ContactOrganizerModal };
